import { useCallback, useEffect, useRef, useState } from "react";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getAtributosFamilia, grabarAtributo, eliminarAtributo } from "../../../services/backend/catalogService";
import useActionState from "./useActionState";
import useDialogState from "./useDialogState";

const useAtributos = ({ selectedFamiliaData, onError, onToast }) => {
    const onErrorRef = useRef(onError);
    const onToastRef = useRef(onToast);
    onErrorRef.current = onError;
    onToastRef.current = onToast;

    const [atributos, setAtributos] = useState([]);
    const [loading, setLoading] = useState(false);

    const agregarDialog = useDialogState();
    const editarDialog = useDialogState();
    const eliminarDialog = useDialogState();
    const valoresDialog = useDialogState();

    const { run } = useActionState({
        onError: (err) => onErrorRef.current?.(err),
    });

    const fetchAtributos = useCallback(async (familiaId) => {
        if (!familiaId) {
            setAtributos([]);
            return;
        }
        setLoading(true);
        try {
            await ensureCatalogAuth();
            const response = await getAtributosFamilia(familiaId);
            const items = Array.isArray(response?.atributos) ? response.atributos
                : Array.isArray(response) ? response : [];
            setAtributos(items);
        } catch (err) {
            setAtributos([]);
            onErrorRef.current?.(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAtributos(selectedFamiliaData?.id);
    }, [selectedFamiliaData?.id, fetchAtributos]);

    const refetch = () => fetchAtributos(selectedFamiliaData?.id);

    const handleAgregar = async (data) => {
        const ok = await run(async () => {
            await grabarAtributo({
                id_familia: selectedFamiliaData?.id,
                llave: data.llave,
                tipo_dato: data.tipo_dato,
            });
        });
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Atributo creado", life: 2500 });
            agregarDialog.close();
            refetch();
        }
    };

    const handleEditar = async (data) => {
        const ok = await run(async () => {
            await grabarAtributo({
                id: data.id,
                id_familia: selectedFamiliaData?.id,
                llave: data.llave,
                tipo_dato: data.tipo_dato,
            });
        });
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Atributo actualizado", life: 2500 });
            editarDialog.close();
            refetch();
        }
    };

    const handleEliminar = async () => {
        const target = eliminarDialog.selectedItem;
        if (!target?.id) return;
        const ok = await run(async () => {
            await eliminarAtributo(target.id);
        });
        eliminarDialog.close();
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Atributo eliminado", life: 2500 });
            if (valoresDialog.visible && valoresDialog.selectedItem?.id === target.id) {
                valoresDialog.close();
            }
            refetch();
        }
    };

    return {
        atributos,
        loading,
        agregarDialog,
        editarDialog,
        eliminarDialog,
        valoresDialog,
        handleAgregar,
        handleEditar,
        handleEliminar,
    };
};

export default useAtributos;
